import type { Anchor } from '@manager/types/vuetify-types';
import { computed, type ComputedRef, type InjectionKey } from 'vue';
import { type RouteRecordRaw, useRouter } from 'vue-router';

export interface IRoutableModalState {
  hasChanges: boolean;
  open: boolean;
  redirectTo?: string;
}

export const routableModalKey = Symbol() as InjectionKey<IRoutableModalState>;

export type IRoutableModalRouteOptions = {
  fullscreen?: boolean;
  width?: 'sm' | 'md' | 'lg' | 'xl' | 'unset';
  origin?: Anchor | 'auto' | 'overlap';
  /** Off hands browser back to the router instead of closing the dialog. */
  closeOnBack?: boolean;
};

export const routableModalDefaultOptions: IRoutableModalRouteOptions = {
  fullscreen: true,
  origin: 'center center',
  width: 'lg',
};

export function getRoutableModalOptions(
  opt?: Partial<IRoutableModalRouteOptions>
): IRoutableModalRouteOptions {
  return {
    ...routableModalDefaultOptions,
    ...(opt || {}),
  };
}

export function useCurrentModalRouteConfig(): ComputedRef<IRoutableModalRouteOptions> {
  const router = useRouter();
  return computed(() =>
    getRoutableModalOptions(router.currentRoute.value?.meta.modal)
  );
}

export function buildModalRoute(
  config: RouteRecordRaw,
  modalConfig?: Partial<IRoutableModalRouteOptions>
): RouteRecordRaw {
  return {
    path: config.path,
    component: () =>
      import('@manager/modules/modals/routable/ModalRoutableContainer.vue'),
    children: [
      {
        ...config,
        path: '',
        meta: {
          ...(config.meta || {}),
          modal: modalConfig ?? {},
        },
      },
    ],
  };
}
